import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { z } from 'zod'
import { MailPlus, Package, RotateCw, Trash2, Users } from 'lucide-react'
import { apiRequest } from '@/lib/api-client'
import { useWorkspace } from '@/features/workspace/workspace-provider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

const roleSchema = z.enum(['owner', 'admin', 'member', 'viewer'])

/** M5-A: Deutsche Rollen-Labels (Werte bleiben englisch). */
export const ROLE_LABELS: Record<z.infer<typeof roleSchema>, string> = {
  owner: 'Besitzer',
  admin: 'Admin',
  member: 'Mitglied',
  viewer: 'Betrachter',
}

function roleLabel(role: string): string {
  return ROLE_LABELS[role as z.infer<typeof roleSchema>] ?? role
}
const memberSchema = z.object({
  workspace_id: z.string(),
  user_id: z.string(),
  email: z.string().nullable().optional(),
  role: roleSchema,
  joined_at: z.string().nullable(),
  created_at: z.string(),
})
const inviteSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: roleSchema,
  expires_at: z.string(),
  created_at: z.string(),
})

export function SettingsTeamPage() {
  const queryClient = useQueryClient()
  const { workspace, isAdmin } = useWorkspace()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<z.infer<typeof roleSchema>>('member')

  const members = useQuery({
    queryKey: ['workspace-members'],
    queryFn: () => apiRequest({ path: '/workspace/members', schema: z.object({ members: z.array(memberSchema) }) }),
  })
  const invites = useQuery({
    queryKey: ['workspace-invites'],
    queryFn: () => apiRequest({ path: '/workspace/invites', schema: z.object({ invites: z.array(inviteSchema) }) }),
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['workspace-members'] })
    void queryClient.invalidateQueries({ queryKey: ['workspace-invites'] })
  }

  const createInvite = useMutation({
    mutationFn: () => apiRequest({ method: 'POST', path: '/workspace/invites', body: { email, role }, schema: inviteSchema }),
    onSuccess: () => {
      setEmail('')
      toast.success('Einladung verschickt.')
      refresh()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Einladung fehlgeschlagen'),
  })

  const sortedMembers = useMemo(() => members.data?.members ?? [], [members.data])

  return (
    // R1: ohne doppelte Aussenpadding-Schicht — mobil 16px Rand.
    <div className="mx-auto w-full max-w-[1180px] space-y-6 px-4 py-6 min-[761px]:px-8 min-[761px]:py-8">
      <header>
        <h1 className="text-[26px] font-[650] leading-tight tracking-tight text-foreground">Team</h1>
        <p className="mt-1 text-sm text-muted-foreground">Mitglieder und offene Workspace-Einladungen.</p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[16px] font-[650]"><Package className="h-4 w-4 shrink-0" />Branchenpaket</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {workspace.sector_pack
              ? `Aktiv: ${workspace.sector_pack.label} (Version ${workspace.sector_pack.version}).`
              : 'Kein Branchenpaket aktiv.'}
          </p>
          {isAdmin ? (
            <Link to="/admin" className="inline-block text-sm text-accent hover:underline">
              Branchenpaket in der Admin-Ansicht auswählen
            </Link>
          ) : (
            <p className="text-sm text-muted-foreground">
              Die Auswahl erfolgt durch einen Admin in der Admin-Ansicht.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[16px] font-[650]"><MailPlus className="h-4 w-4 shrink-0" />Einladen</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="grid gap-3 min-[761px]:grid-cols-[1fr_160px_auto]" onSubmit={(event) => { event.preventDefault(); createInvite.mutate() }}>
            <div className="space-y-2">
              <Label htmlFor="invite-email">E-Mail</Label>
              <Input id="invite-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Rolle</Label>
              <select
                id="invite-role"
                className="h-11 min-[761px]:h-9 w-full rounded-md border border-input bg-background px-3 text-[16px] min-[761px]:text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={role}
                onChange={(event) => setRole(event.target.value as z.infer<typeof roleSchema>)}
              >
                <option value="admin">Admin</option>
                <option value="member">Mitglied</option>
                <option value="viewer">Betrachter</option>
              </select>
            </div>
            <Button className="self-end" disabled={createInvite.isPending}>Einladen</Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[16px] font-[650]"><Users className="h-4 w-4 shrink-0" />Mitglieder</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {sortedMembers.map((member) => (
            <div key={member.user_id} className="grid gap-3 rounded-md border p-3 min-[761px]:grid-cols-[1fr_160px] min-[761px]:items-center">
              <div className="min-w-0">
                <p className="break-words text-sm font-medium">{member.email ?? 'Mitglied'}</p>
                <p className="text-xs text-muted-foreground">
                  {roleLabel(member.role)} · Beigetreten {member.joined_at ? new Date(member.joined_at).toLocaleDateString('de-CH') : 'offen'}
                </p>
              </div>
              <RoleSelect userId={member.user_id} role={member.role} onDone={refresh} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-[16px] font-[650]">Offene Einladungen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(invites.data?.invites ?? []).map((invite) => (
            <div key={invite.id} className="grid gap-3 rounded-md border p-3 min-[761px]:grid-cols-[1fr_auto_auto] min-[761px]:items-center">
              <div className="min-w-0">
                <p className="break-words text-sm font-medium">{invite.email}</p>
                <p className="text-xs text-muted-foreground">{roleLabel(invite.role)} bis {new Date(invite.expires_at).toLocaleDateString('de-CH')}</p>
              </div>
              <InviteAction token={invite.id} mode="resend" onDone={refresh} />
              <InviteAction token={invite.id} mode="delete" onDone={refresh} />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

function RoleSelect({ userId, role, onDone }: { userId: string; role: z.infer<typeof roleSchema>; onDone: () => void }) {
  const [value, setValue] = useState(role)
  async function update(next: string) {
    setValue(next as z.infer<typeof roleSchema>)
    try {
      await apiRequest({ method: 'PATCH', path: `/workspace/members/${userId}`, body: { role: next }, schema: memberSchema })
      toast.success('Rolle aktualisiert.')
      onDone()
    } catch (err) {
      setValue(role)
      toast.error(err instanceof Error ? err.message : 'Update fehlgeschlagen')
    }
  }
  return (
    <select
      className="h-11 min-[761px]:h-9 w-full rounded-md border border-input bg-background px-3 text-[16px] min-[761px]:text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      aria-label="Rolle"
      value={value}
      onChange={(event) => void update(event.target.value)}
    >
      <option value="owner">Besitzer</option>
      <option value="admin">Admin</option>
      <option value="member">Mitglied</option>
      <option value="viewer">Betrachter</option>
    </select>
  )
}

function InviteAction({ token, mode, onDone }: { token: string; mode: 'resend' | 'delete'; onDone: () => void }) {
  async function run() {
    try {
      if (mode === 'resend') {
        await apiRequest({ method: 'POST', path: `/workspace/invites/${token}/resend`, schema: z.object({ status: z.string() }) })
      } else {
        await apiRequest({ method: 'DELETE', path: `/workspace/invites/${token}`, schema: z.null() })
      }
      toast.success(mode === 'resend' ? 'Erneut gesendet.' : 'Einladung widerrufen.')
      onDone()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Aktion fehlgeschlagen')
    }
  }
  const Icon = mode === 'resend' ? RotateCw : Trash2
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      aria-label={mode === 'resend' ? 'Einladung erneut senden' : 'Einladung widerrufen'}
      onClick={() => void run()}
    >
      <Icon className="h-4 w-4" />
    </Button>
  )
}
